export interface CoreEndpoint {
  path: string;
  description: string;
}

export interface CoreEndpointCategory {
  category: string;
  endpoints: CoreEndpoint[];
}

// Internal TWM API — Flask routes from backend/app.py + portal_api.py + dev_api.py.
// Paths use {name} placeholders; ApiTools substitutes from the request-params form.
// Method prefixes are normalized to GET/POST/PUT/PATCH/DELETE.
export const coreEndpoints: CoreEndpointCategory[] = [
  { category: 'System & Auth', endpoints: [
    { path: 'GET /system/health', description: 'Liveness probe' },
    { path: 'POST /auth/login', description: 'Employee login (returns JWT)' },
    { path: 'POST /auth/set-password', description: 'Set password from invite/reset token' },
    { path: 'GET /auth/me', description: 'Current employee profile' },
    { path: 'PUT /auth/me', description: 'Update current employee profile' },
    { path: 'POST /auth/change-password', description: 'Change password (authenticated)' },
    { path: 'GET /get-intercom-token', description: 'Issue Intercom identity token' },
  ]},
  { category: 'Customers', endpoints: [
    { path: 'GET /customers', description: 'List customers' },
    { path: 'GET /customers/{customer_id}', description: 'Fetch customer' },
    { path: 'GET /customers/{customer_id}/orders', description: 'Orders for customer' },
    { path: 'GET /customers/{customer_id}/documents', description: 'Documents for customer' },
    { path: 'GET /customers/{customer_id}/contacts', description: 'Contacts for customer' },
    { path: 'GET /customers/{customer_id}/payment-methods', description: 'Payment methods for customer' },
    { path: 'GET /customers/with-payment-methods', description: 'Customers having any payment method on file' },
    { path: 'PUT /customers/{customer_id}/company', description: 'Update customer company' },
    { path: 'POST /customers/sync', description: 'Sync customers from Booqable' },
    { path: 'GET /customers/import/template', description: 'Download CSV import template' },
    { path: 'POST /customers/import', description: 'Bulk-import customers from CSV' },
  ]},
  { category: 'Customer Locations', endpoints: [
    { path: 'GET /customer-locations', description: 'List all customer locations' },
    { path: 'GET /customers/{customer_id}/locations', description: 'List a customer’s locations' },
    { path: 'POST /customers/{customer_id}/locations', description: 'Add a customer location' },
    { path: 'PUT /customers/{customer_id}/locations/{location_id}', description: 'Update a customer location' },
    { path: 'PATCH /customers/{customer_id}/locations/{location_id}', description: 'Patch a customer location' },
    { path: 'DELETE /customers/{customer_id}/locations/{location_id}', description: 'Delete a customer location' },
  ]},
  { category: 'Orders', endpoints: [
    { path: 'GET /orders', description: 'List orders' },
    { path: 'GET /orders/{order_id}', description: 'Fetch order' },
    { path: 'PUT /orders/{order_id}', description: 'Update order (delivery address, dates, etc.)' },
    { path: 'GET /orders/concept', description: 'Concept (draft preview) order' },
    { path: 'POST /orders/draft', description: 'Create a draft order directly in Booqable' },
    { path: 'POST /orders/local-draft', description: 'Create a Supabase-only draft (no Booqable order yet)' },
    { path: 'POST /orders/{order_id}/promote', description: 'Promote a local draft to a real Booqable order' },
    { path: 'POST /orders/sync', description: 'Sync orders from Booqable' },
  ]},
  { category: 'Products & Inventory', endpoints: [
    { path: 'POST /products/sync', description: 'Sync products from Booqable' },
  ]},
  { category: 'Pricing & Tiers', endpoints: [
    { path: 'GET /rolloff-rates/lookup', description: 'Tier + per-size rates for a PR municipality (?municipality=)' },
    { path: 'GET /pricing/resolve', description: 'Resolve tier-aware unit price for a product (?order_id=&product_group_id=)' },
  ]},
  { category: 'Tax Regions', endpoints: [
    { path: 'GET /tax-regions', description: 'List tax regions' },
    { path: 'POST /tax-regions/sync', description: 'Sync tax regions from Booqable' },
  ]},
  { category: 'Tasks', endpoints: [
    { path: 'GET /tasks', description: 'List tasks' },
    { path: 'GET /tasks/{task_id}', description: 'Fetch task' },
    { path: 'GET /tasks/order/{order_id}', description: 'Tasks for an order' },
    { path: 'POST /tasks', description: 'Create task' },
    { path: 'PUT /tasks/{task_id}', description: 'Update task' },
    { path: 'PATCH /tasks/{task_id}', description: 'Patch task' },
    { path: 'DELETE /tasks/{task_id}', description: 'Delete task' },
    { path: 'POST /tasks/sync-from-orders', description: 'Generate tasks from current orders' },
  ]},
  { category: 'Activities', endpoints: [
    { path: 'GET /activities', description: 'List activities' },
    { path: 'GET /customers/{entity_id}/activities', description: 'Activities for a customer' },
    { path: 'GET /activities/{entity_type}/{entity_id}', description: 'Activities for any entity' },
  ]},
  { category: 'Messages', endpoints: [
    { path: 'GET /staff/messages/unread', description: 'Unread-count summary for staff' },
    { path: 'GET /staff/messages/conversations', description: 'List staff conversations' },
    { path: 'POST /staff/messages/conversations', description: 'Create staff conversation' },
    { path: 'GET /conversations/{conv_id}/messages', description: 'List messages in conversation' },
    { path: 'POST /conversations/{conv_id}/messages', description: 'Post a message' },
    { path: 'DELETE /conversations/{conv_id}/messages', description: 'Delete a message from conversation' },
    { path: 'GET /get-messages', description: 'Legacy messages endpoint' },
  ]},
  { category: 'Bookings (Website)', endpoints: [
    { path: 'POST /bookings/draft', description: 'Create draft booking from website' },
    { path: 'POST /bookings/create', description: 'Finalize a booking + initiate payment' },
    { path: 'GET /bookings/payment-return', description: 'Return URL after AN hosted checkout' },
    { path: 'POST /bookings/payment-return', description: 'Return URL after AN hosted checkout' },
    { path: 'GET /bookings/repay/info', description: 'Lookup info for repay link' },
    { path: 'POST /bookings/repay', description: 'Re-pay a failed booking' },
    { path: 'POST /bookings/confirm', description: 'Confirm a paid booking' },
  ]},
  { category: 'Jobs', endpoints: [
    { path: 'POST /jobs/{job_name}/run', description: 'Run a scheduled job ad-hoc' },
    { path: 'GET /jobs/runs', description: 'List recent job runs' },
  ]},
  { category: 'Search', endpoints: [
    { path: 'GET /global-search', description: 'Cross-entity search (?q=)' },
  ]},
  { category: 'Booqable Wrappers', endpoints: [
    { path: 'GET /booqable/test', description: 'Booqable connectivity check' },
    { path: 'GET /booqable/get-customers', description: 'List customers via Booqable' },
    { path: 'GET /booqable/customers/{customer_id}', description: 'Fetch customer via Booqable' },
    { path: 'PATCH /booqable/customers/{customer_id}', description: 'Update customer via Booqable' },
    { path: 'DELETE /booqable/customers/{customer_id}', description: 'Delete customer via Booqable' },
    { path: 'GET /booqable/get-customer-orders/{customer_id}', description: 'Orders for customer via Booqable' },
    { path: 'GET /booqable/get-customer-documents/{customer_id}', description: 'Documents for customer via Booqable' },
    { path: 'GET /booqable/orders', description: 'List Booqable orders' },
    { path: 'GET /booqable/orders/{order_id}', description: 'Fetch Booqable order' },
    { path: 'PUT /booqable/orders/{order_id}', description: 'Update Booqable order' },
    { path: 'PATCH /booqable/orders/{order_id}', description: 'Patch Booqable order' },
    { path: 'PUT /booqable/orders/{order_id}/billing-address', description: 'Update order billing address' },
    { path: 'POST /booqable/orders/{order_id}/reserve', description: 'Reserve order in Booqable' },
    { path: 'POST /booqable/orders/{order_id}/send-confirmation', description: 'Manually send the booking-confirmation email (cutover)' },
    { path: 'PUT /booqable/orders/{order_id}/archive', description: 'Archive order in Booqable' },
    { path: 'POST /booqable/orders/{order_id}/archive', description: 'Archive order in Booqable' },
    { path: 'POST /booqable/orders/{order_id}/lines', description: 'Add a line to a Booqable order (tier-aware)' },
    { path: 'GET /booqable/orders/{order_id}/documents', description: 'Documents on a Booqable order' },
    { path: 'PUT /booqable/lines/{line_id}', description: 'Update an order line' },
    { path: 'PATCH /booqable/lines/{line_id}', description: 'Patch an order line' },
    { path: 'DELETE /booqable/lines/{line_id}', description: 'Delete an order line' },
    { path: 'GET /booqable/products', description: 'List Booqable products' },
    { path: 'GET /booqable/product-groups', description: 'List Booqable product groups' },
    { path: 'GET /booqable/product-group/{product_group_id}', description: 'Fetch Booqable product group' },
    { path: 'PUT /booqable/product-groups/{product_group_id}', description: 'Update product group' },
    { path: 'PATCH /booqable/product-groups/{product_group_id}', description: 'Patch product group' },
    { path: 'GET /booqable/product-groups/{product_group_id}/variations', description: 'Variations for product group' },
    { path: 'GET /booqable/products/{product_id}/availability', description: 'Availability for product' },
    { path: 'GET /booqable/stock-items-with-products', description: 'Stock items joined with products' },
    { path: 'GET /booqable/stock-item/{stock_item_id}/plannings', description: 'Plannings for a stock item' },
    { path: 'POST /booqable/inventory-levels', description: 'Inventory level adjustments' },
    { path: 'GET /booqable/documents', description: 'List Booqable documents' },
    { path: 'GET /booqable/documents/{document_id}', description: 'Fetch Booqable document' },
    { path: 'POST /booqable/documents/{document_id}/finalize', description: 'Finalize Booqable document' },
    { path: 'GET /booqable/tax-regions', description: 'Tax regions from Booqable' },
    { path: 'GET /booqable/properties', description: 'List Booqable properties' },
    { path: 'PUT /booqable/properties/{property_id}', description: 'Update Booqable property' },
    { path: 'PATCH /booqable/properties/{property_id}', description: 'Patch Booqable property' },
    { path: 'POST /booqable/create/{resource_path}', description: 'Generic create proxy' },
  ]},
  { category: 'Linxup Wrappers', endpoints: [
    { path: 'GET /linxup/customers', description: 'Linxup customers (cached)' },
    { path: 'GET /linxup/locations', description: 'Linxup device locations' },
    { path: 'GET /linxup/groups', description: 'Linxup device groups' },
  ]},
  { category: 'Portal — Auth', endpoints: [
    { path: 'POST /portal/auth/login', description: 'Customer portal login' },
    { path: 'POST /portal/auth/register', description: 'Customer portal register' },
    { path: 'POST /portal/auth/refresh', description: 'Refresh portal token' },
    { path: 'POST /portal/auth/logout', description: 'Portal logout' },
    { path: 'POST /portal/auth/set-password', description: 'Portal set password' },
    { path: 'POST /portal/sync', description: 'Sync portal customer state' },
  ]},
  { category: 'Portal — Data', endpoints: [
    { path: 'GET /portal/dashboard', description: 'Portal dashboard payload' },
    { path: 'GET /portal/orders', description: 'Portal orders list' },
    { path: 'GET /portal/orders/{order_id}', description: 'Portal order detail' },
    { path: 'GET /portal/tracking/{order_id}', description: 'Live-tracking feed for an order' },
    { path: 'GET /portal/documents', description: 'Portal documents list' },
    { path: 'GET /portal/documents/{document_id}', description: 'Portal document detail' },
    { path: 'GET /portal/activity', description: 'Portal activity feed' },
  ]},
  { category: 'Portal — Messages', endpoints: [
    { path: 'GET /portal/messages/conversations', description: 'List portal conversations' },
    { path: 'POST /portal/messages/conversations', description: 'Create portal conversation' },
    { path: 'GET /portal/messages/{conversation_id}', description: 'List messages in conversation' },
    { path: 'POST /portal/messages/{conversation_id}', description: 'Post message in conversation' },
    { path: 'GET /portal/messages/{conversation_id}/typing', description: 'Typing indicator stream' },
  ]},
  { category: 'Portal — Profile', endpoints: [
    { path: 'GET /portal/profile', description: 'Portal customer profile' },
    { path: 'PATCH /portal/profile', description: 'Update portal profile' },
    { path: 'POST /portal/profile/password', description: 'Change portal password' },
    { path: 'GET /portal/profile/addresses', description: 'List portal addresses' },
    { path: 'POST /portal/profile/addresses', description: 'Add portal address' },
    { path: 'PATCH /portal/profile/addresses/{address_id}', description: 'Update portal address' },
    { path: 'DELETE /portal/profile/addresses/{address_id}', description: 'Delete portal address' },
  ]},
  { category: 'Dev Tools', endpoints: [
    { path: 'POST /dev/capture', description: 'Capture a dev event/payload for review' },
    { path: 'POST /dev/briefing', description: 'Generate a dev briefing' },
  ]},
];
